import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  AudienceCommunitySettings,
  CommunitySettings,
  mergeCommunitySettings,
} from '../../constants/communitySettingsDefaults';

interface Props {
  settings: CommunitySettings;
  onChange: (settings: CommunitySettings) => void;
  onSave: () => Promise<void>;
  saving: boolean;
}

type AudienceKey = 'aspirant' | 'mentor' | 'hackathon';

const AUDIENCE_LABELS: Record<AudienceKey, { title: string; hint: string }> = {
  aspirant: {
    title: 'Aspirants',
    hint: 'Included in aspirant approval emails',
  },
  mentor: {
    title: 'Mentors',
    hint: 'Included in mentor approval emails',
  },
  hackathon: {
    title: 'Hackathon',
    hint: 'Included in hackathon registration emails',
  },
};

const validateWhatsAppLink = (link: string) =>
  !link || link.startsWith('https://chat.whatsapp.com/') || link.startsWith('https://wa.me/');

const validateDiscordInvite = (url: string) =>
  !url || url.startsWith('https://discord.gg/') || url.startsWith('https://discord.com/invite/');

const validateWebhook = (url: string) =>
  !url || url.startsWith('https://discord.com/api/webhooks/');

const AudienceSection: React.FC<{
  audienceKey: AudienceKey;
  audience: AudienceCommunitySettings;
  onUpdate: (audience: AudienceCommunitySettings) => void;
  errors: string[];
}> = ({ audienceKey, audience, onUpdate, errors }) => {
  const { title, hint } = AUDIENCE_LABELS[audienceKey];

  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200 space-y-4">
      <div>
        <h4 className="font-semibold text-gray-800">{title}</h4>
        <p className="text-xs text-gray-500">{hint}</p>
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={`${audienceKey}-platform`}
            checked={audience.platform === 'whatsapp'}
            onChange={() => onUpdate({ ...audience, platform: 'whatsapp' })}
            className="text-green-600"
          />
          <span className="text-sm font-medium text-gray-700">WhatsApp</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={`${audienceKey}-platform`}
            checked={audience.platform === 'discord'}
            onChange={() => onUpdate({ ...audience, platform: 'discord' })}
            className="text-indigo-600"
          />
          <span className="text-sm font-medium text-gray-700">Discord</span>
        </label>
      </div>

      {audience.platform === 'whatsapp' ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Group Link</label>
          <Input
            value={audience.whatsappLink}
            onChange={(e) => onUpdate({ ...audience, whatsappLink: e.target.value })}
            placeholder="https://chat.whatsapp.com/..."
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Discord Invite URL</label>
          <Input
            value={audience.discordInviteUrl}
            onChange={(e) => onUpdate({ ...audience, discordInviteUrl: e.target.value })}
            placeholder="https://discord.gg/..."
          />
        </div>
      )}

      {errors.map((err) => (
        <p key={err} className="text-sm text-red-600">{err}</p>
      ))}
    </div>
  );
};

const CommunitySettingsEditor: React.FC<Props> = ({ settings, onChange, onSave, saving }) => {
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const updateAudience = (key: AudienceKey, audience: AudienceCommunitySettings) => {
    onChange({ ...settings, [key]: audience });
    setErrors((prev) => ({ ...prev, [key]: [] }));
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string[]> = {};
    let valid = true;

    (['aspirant', 'mentor', 'hackathon'] as AudienceKey[]).forEach((key) => {
      const aud = settings[key];
      const keyErrors: string[] = [];
      if (aud.platform === 'whatsapp') {
        if (!aud.whatsappLink.trim()) keyErrors.push('WhatsApp link is required');
        else if (!validateWhatsAppLink(aud.whatsappLink)) keyErrors.push('Invalid WhatsApp link format');
      } else {
        if (!aud.discordInviteUrl.trim()) keyErrors.push('Discord invite URL is required');
        else if (!validateDiscordInvite(aud.discordInviteUrl)) keyErrors.push('Invalid Discord invite URL');
      }
      if (keyErrors.length) {
        nextErrors[key] = keyErrors;
        valid = false;
      }
    });

    if (settings.discord.jobWebhookUrl.trim() && !validateWebhook(settings.discord.jobWebhookUrl)) {
      nextErrors.discord = ['Invalid Discord webhook URL'];
      valid = false;
    }

    setErrors(nextErrors);
    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;
    await onSave();
  };

  return (
    <Card className="p-6 space-y-6 bg-blue-50 border border-blue-100">
      <div>
        <h3 className="text-lg font-semibold text-blue-800">Community Platforms</h3>
        <p className="text-sm text-gray-600 mt-1">
          Choose WhatsApp or Discord for each audience. Approval and registration emails use the selected platform.
        </p>
      </div>

      <div className="space-y-4">
        {(['aspirant', 'mentor', 'hackathon'] as AudienceKey[]).map((key) => (
          <AudienceSection
            key={key}
            audienceKey={key}
            audience={settings[key]}
            onUpdate={(aud) => updateAudience(key, aud)}
            errors={errors[key] || []}
          />
        ))}
      </div>

      <div className="p-4 bg-white rounded-lg border border-gray-200 space-y-2">
        <h4 className="font-semibold text-gray-800">Job Board Webhook</h4>
        <p className="text-xs text-gray-500">
          When an admin approves a job, a summary is posted to this Discord channel webhook.
        </p>
        <Input
          value={settings.discord.jobWebhookUrl}
          onChange={(e) =>
            onChange({
              ...settings,
              discord: { ...settings.discord, jobWebhookUrl: e.target.value },
            })
          }
          placeholder="https://discord.com/api/webhooks/... (optional)"
        />
        {(errors.discord || []).map((err) => (
          <p key={err} className="text-sm text-red-600">{err}</p>
        ))}
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
        {saving ? 'Saving...' : 'Save Community Settings'}
      </Button>
    </Card>
  );
};

export default CommunitySettingsEditor;
